import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { 
  Bot, 
  Brain, 
  Cpu, 
  ShieldCheck, 
  TrendingUp, 
  ArrowRight, 
  CheckCircle2, 
  Layers, 
  Zap,
  GlobeLock,
  PackageCheck
} from 'lucide-react';

const Landing = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleDownloadClick = (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) {
      alert(t('landing.security_warning'));
      navigate('/login');
    } else {
      window.location.href = "/orchestrator/agent-download/";
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-sans selection:bg-cyan-500 selection:text-slate-950 overflow-x-hidden">
      
      {/* Background Glows */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none animate-pulse"></div>
      <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none animate-pulse delay-1000"></div>

      {/* Navbar */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-slate-950/80 border-b border-slate-800/80 transition-all">
        <div className="w-full px-6 lg:px-12 h-24 flex items-center justify-between">
          <div className="flex flex-col items-center justify-center group cursor-pointer" onClick={scrollToTop}>
            <img src="/logo.png" alt="Robopam Logo" className="w-12 h-12 object-contain drop-shadow-[0_0_20px_rgba(6,182,212,0.6)] group-hover:scale-105 transition-transform duration-300" />
            <span className="text-xl font-black tracking-widest bg-gradient-to-r from-cyan-400 via-indigo-300 to-indigo-400 bg-clip-text text-transparent mt-0.5 leading-none">
              ROBOPAM
            </span>
          </div>

          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-300">
            <a href="#ozellikler" className="hover:text-cyan-400 transition-colors">{t('landing.nav_features')}</a>
            <a href="https://www.yapbirobot.com" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors">{t('landing.nav_examples')}</a>
          </nav>

          <div className="flex items-center gap-4">
            <LanguageSwitcher darkMode={true} />
            <Link 
              to="/login" 
              className="relative group overflow-hidden px-6 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-indigo-600 font-bold text-sm text-white shadow-xl shadow-cyan-500/20 hover:shadow-cyan-500/40 transition-all duration-300 active:scale-95 flex items-center gap-2"
            >
              <span className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform"></span>
              <span className="relative z-10 flex items-center gap-2">
                {t('landing.login_btn')} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-24 pb-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">

          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/80 border border-slate-800 text-xs font-semibold text-cyan-400 mb-8 shadow-inner">
            {t('landing.badge')}
          </div>

          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tight text-slate-100 max-w-6xl mx-auto leading-tight mb-8">
            {t('landing.hero_title_1')} <br />
            <span className="bg-gradient-to-r from-cyan-400 via-indigo-400 to-purple-500 bg-clip-text text-transparent">
              {t('landing.hero_title_2')}
            </span>
          </h1>

          <p className="text-lg md:text-xl text-slate-400 max-w-3xl mx-auto mb-12 font-light leading-relaxed">
            {t('landing.hero_desc')}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
            <Link
              to="/login"
              className="w-full sm:w-auto px-8 py-4 rounded-2xl bg-gradient-to-r from-cyan-500 via-indigo-600 to-indigo-700 font-extrabold text-white text-base shadow-2xl shadow-indigo-500/30 hover:shadow-cyan-500/50 hover:scale-105 transition-all flex items-center justify-center gap-3 group"
            >
              {t('landing.start')} <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <button
              onClick={handleDownloadClick}
              className="w-full sm:w-auto px-8 py-4 rounded-2xl bg-slate-900 border border-slate-800 font-bold text-slate-300 text-base hover:bg-slate-800 hover:border-slate-700 hover:text-white transition-all flex items-center justify-center gap-3 shadow-lg cursor-pointer"
            >
              <Bot className="w-5 h-5 text-cyan-400" /> {t('landing.download_agent')}
            </button>
          </div>

          {/* Hero Dashboard Preview Mockup */}
          <div className="mt-20 relative mx-auto max-w-5xl rounded-3xl p-3 bg-gradient-to-b from-slate-800/80 to-slate-900/40 border border-slate-700/50 shadow-2xl backdrop-blur-xl">
            <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500/10 to-indigo-500/10 rounded-3xl filter blur-xl pointer-events-none"></div>
            <div className="bg-slate-950 rounded-2xl overflow-hidden border border-slate-800 shadow-inner">
              <div className="h-10 bg-slate-900/80 border-b border-slate-800 flex items-center px-4 gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
                <div className="w-3 h-3 rounded-full bg-green-500/80"></div>
                <span className="text-xs text-slate-500 font-mono ml-4">robopam-orchestrator-console</span>
              </div>
              <div className="p-6 text-left font-mono text-sm text-slate-300 space-y-3">
                <div className="flex items-center gap-2 text-cyan-400">
                  <span>❯</span>
                  <span>[AGENT-CHECKIN] Robot ROBOPAM-WIN11 (ID: 4192) başarıyla bağlandı.</span>
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <span>❯</span>
                  <span>[AST-PARSER] Kod içindeki importlar tarandı: {'{selenium, pandas, openpyxl}'}.</span>
                </div>
                <div className="flex items-center gap-2 text-emerald-400">
                  <span>❯</span>
                  <span>[AUTO-PIP] C:\ProgramData\Robopam\rpa_env üzerine kütüphaneler başarıyla yüklendi.</span>
                </div>
                <div className="flex items-center gap-2 text-indigo-300">
                  <span>❯</span>
                  <span>[EXECUTION] Görev (Fatura_Aktarim_v2) 0 hata ve %100 kararlılık ile tamamlandı!</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Neden Comel RPA Section */}
      <section id="ozellikler" className="py-32 relative border-t border-slate-900 bg-slate-950/50 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-3xl md:text-5xl font-black text-slate-100 mb-6">
              {t('landing.why_robopam')}
            </h2>
            <p className="text-slate-400 text-lg font-light">
              {t('landing.why_desc')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            {/* Card 1 */}
            <div className="p-8 rounded-3xl bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl hover:border-cyan-500/50 transition-all group hover:-translate-y-2 shadow-xl shadow-slate-950">
              <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 mb-6 group-hover:scale-110 transition-transform">
                <Brain className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold text-slate-100 mb-4">{t('landing.card1_title')}</h3>
              <p className="text-slate-400 leading-relaxed font-light">
                {t('landing.card1_desc')}
              </p>
            </div>

            {/* Card 2 */}
            <div className="p-8 rounded-3xl bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl hover:border-indigo-500/50 transition-all group hover:-translate-y-2 shadow-xl shadow-slate-950">
              <div className="w-14 h-14 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400 mb-6 group-hover:scale-110 transition-transform">
                <GlobeLock className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold text-slate-100 mb-4">{t('landing.card2_title')}</h3>
              <p className="text-slate-400 leading-relaxed font-light">
                {t('landing.card2_desc')}
              </p>
            </div>

            {/* Card 3 */}
            <div className="p-8 rounded-3xl bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl hover:border-purple-500/50 transition-all group hover:-translate-y-2 shadow-xl shadow-slate-950">
              <div className="w-14 h-14 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 mb-6 group-hover:scale-110 transition-transform">
                <PackageCheck className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold text-slate-100 mb-4">{t('landing.card3_title')}</h3>
              <p className="text-slate-400 leading-relaxed font-light">
                {t('landing.card3_desc')}
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-indigo-500/10 to-purple-500/10 filter blur-3xl"></div>
        <div className="max-w-5xl mx-auto px-6 text-center relative z-10 bg-slate-900/80 border border-slate-800 p-16 rounded-3xl shadow-2xl backdrop-blur-xl">
          <h2 className="text-4xl md:text-6xl font-black text-slate-100 mb-6 tracking-tight">
            {t('landing.cta_title_1')} <br />
            <span className="text-cyan-400">{t('landing.cta_title_2')}</span>
          </h2>
          <p className="text-slate-400 text-lg mb-10 max-w-2xl mx-auto font-light leading-relaxed">
            {t('landing.cta_desc')}
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-3 px-10 py-5 rounded-2xl bg-gradient-to-r from-cyan-500 via-indigo-600 to-indigo-700 font-extrabold text-white text-lg shadow-2xl shadow-cyan-500/30 hover:scale-105 transition-transform"
          >
            {t('landing.cta_btn')} <ArrowRight className="w-6 h-6" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-900 bg-slate-950 py-12 text-slate-500 text-sm">
        <div className="max-w-7xl mx-auto px-6 flex flex-col items-center justify-center gap-4 text-center">
          <div className="flex flex-col items-center justify-center">
            <img src="/logo.png" alt="Robopam Logo" className="w-10 h-10 object-contain mb-1" />
          </div>
          <p>
            {t('landing.footer_dev')}
          </p>
        </div>
      </footer>

    </div>
  );
};

export default Landing;
